import { useEffect, useRef } from "react";
import {
  useCompanyFiltersStore,
  useCompanyFiltersStoreApi,
} from "../../provider/CompanyFilterProvider";
import { searchCollections } from "@/apis/bacchus/collections";

/**
 * Hydrates intent collection display name when collectionId is present.
 *
 * We persist only `collectionId` (stable) and resolve `collectionName`
 * dynamically (can change).
 */
export function useIntentCollectionHydration(): void {
  const slice = useCompanyFiltersStore((s) => s.slices.intent);
  const storeApi = useCompanyFiltersStoreApi();

  const requestRef = useRef<string | null>(null);

  useEffect(() => {
    if (!slice.collectionId || slice.collectionName) return;

    const requestKey = slice.collectionId;
    if (requestRef.current === requestKey) return;
    requestRef.current = requestKey;

    // Bacchus Flop filters currently don't allow filtering by `id`,
    // so we list and resolve by id client-side.
    searchCollections("intent", { search: "", page: 1, pageSize: 1000 })
      .then(({ data }) => {
        if (requestRef.current !== requestKey) return;

        const latestSlice = storeApi.getState().slices.intent;
        if (latestSlice.collectionId !== requestKey) return;
        if (latestSlice.collectionName) return;

        // Only clear collectionId when we're sure the request succeeded and
        // the collection no longer exists.
        if (!data) return;

        const match = data.find((c) => c.id === requestKey);
        const name = match?.name?.trim();
        if (name) {
          storeApi.getState().setSlice("intent", {
            ...latestSlice,
            collectionName: name,
          });
          return;
        }

        if (match) return;

        // Collection was deleted; keep topics but stop treating the collection
        // as "applied" so users can search/select normally.
        storeApi.getState().setSlice("intent", {
          ...latestSlice,
          collectionId: null,
          collectionName: null,
        });
      })
      .catch(() => {
        // Ignore failures; UI will fallback to topic-based labels.
      })
      .finally(() => {
        if (requestRef.current === requestKey) {
          requestRef.current = null;
        }
      });
  }, [slice.collectionId, slice.collectionName, storeApi]);
}
